"use client";

import { useEffect, useMemo } from "react";
import {
  PHOTO_VIEWS_MAX_EVENTS,
  type PhotoViewEvent,
  type PhotoViewsRecordBody,
} from "@openings/api-contract/photos";
import { getApiClient } from "./apiClient";

let pageSessionId: string | undefined;
function getPageSessionId() {
  // A document-lifetime identifier, shared across galleries and modal mounts.
  // No cookies/localStorage; a reload starts a new visit.
  return (pageSessionId ??= crypto.randomUUID());
}

export function createPhotoViewQueue(
  salonId: string | null | undefined,
  send: (body: PhotoViewsRecordBody) => Promise<unknown>,
  getSessionId = getPageSessionId,
) {
  const seen = new Set<string>();
  let pending: PhotoViewEvent[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => {
    clearTimeout(timer);
    timer = undefined;
    if (!salonId || !pending.length) return;
    const events = pending;
    pending = [];
    let sessionId: string;
    try {
      sessionId = getSessionId();
    } catch {
      return;
    }
    for (let i = 0; i < events.length; i += PHOTO_VIEWS_MAX_EVENTS) {
      const body = {
        salonId,
        sessionId,
        events: events.slice(i, i + PHOTO_VIEWS_MAX_EVENTS),
      };
      // Retrying the same page session cannot double count server-side.
      void Promise.resolve()
        .then(() => send(body))
        .catch((error: unknown) => {
          // Do not immediately retry validation/registration errors or rate limits.
          const status = (error as { status?: number } | null)?.status;
          if (status && status < 500) return;
          return send(body);
        })
        .catch(() => {});
    }
  };
  const enqueue = (
    photoId: string | null | undefined,
    kind: PhotoViewEvent["kind"],
  ) => {
    if (!salonId || !photoId) return;
    const key = `${photoId}:${kind}`;
    if (seen.has(key)) return;
    seen.add(key);
    pending.push({ photoId, kind });
    if (pending.length >= PHOTO_VIEWS_MAX_EVENTS) flush();
    else timer ??= setTimeout(flush, 1000);
  };
  return {
    flush,
    trackImpression: (id: string | null | undefined) =>
      enqueue(id, "impression"),
    trackOpen: (id: string | null | undefined) => enqueue(id, "open"),
  };
}

export function usePhotoViewTracker(salonId: string | null | undefined) {
  const queue = useMemo(
    () =>
      createPhotoViewQueue(salonId, async (body) =>
        getApiClient().photos.recordViews(body),
      ),
    [salonId],
  );
  useEffect(() => {
    if (!salonId) return;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") queue.flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", queue.flush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", queue.flush);
      queue.flush();
    };
  }, [queue, salonId]);
  return queue;
}
